import React from 'react';
import { Box, ButtonBase, IconButton, Typography, useTheme } from '@mui/material';
import { HeartIcon, ChatCircleIcon } from '@phosphor-icons/react';
import { font } from '../../utils/fontHelper';

interface PostActionBarProps {
  likeCount: number;
  isLiked: boolean;
  commentCount: number;
  onLikeToggle: () => void;
  onCommentClick: () => void;
  /** If provided, the like-count number opens a "Liked by" sheet. Disabled visually if absent. */
  onLikedByClick?: () => void;
  /** Disable interaction (e.g. while not signed in). Counts still render. */
  disabled?: boolean;
  /** Reduce icon/text sizes for use inside dense surfaces. */
  dense?: boolean;
}

/**
 * Reusable like + comment action row. Heart toggles like in place (no dialog).
 * Comment icon/count opens the surrounding dialog. Like count opens LikedByBottomSheet.
 *
 * Used in: CrewFeedItem (every social post row), PublicReviewCard, SocialPostDialog body,
 * WatchlistDetail header. The same widget everywhere keeps the visual language consistent.
 */
const PostActionBar: React.FC<PostActionBarProps> = ({
  likeCount,
  isLiked,
  commentCount,
  onLikeToggle,
  onCommentClick,
  onLikedByClick,
  disabled,
  dense,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const iconSize = dense ? 17 : 20;
  const fontSize = dense ? 12 : 13;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <IconButton
        size="small"
        disabled={disabled}
        onClick={(e) => { e.stopPropagation(); onLikeToggle(); }}
        sx={{
          color: isLiked ? '#e91e63' : theme.palette.text.secondary,
          p: dense ? 0.5 : 0.75,
          '&:hover': {
            bgcolor: isLiked
              ? 'rgba(233,30,99,0.1)'
              : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
          },
        }}
        aria-label={isLiked ? 'Unlike' : 'Like'}
      >
        <HeartIcon size={iconSize} weight={isLiked ? 'fill' : 'regular'} />
      </IconButton>
      {likeCount > 0 && (
        <ButtonBase
          onClick={(e) => { e.stopPropagation(); onLikedByClick?.(); }}
          disabled={!onLikedByClick || disabled}
          sx={{
            borderRadius: 1,
            px: 0.75,
            py: 0.5,
            mr: 0.75,
            '&:hover': onLikedByClick && !disabled
              ? { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' }
              : undefined,
          }}
        >
          <Typography sx={{
            fontSize,
            color: theme.palette.text.secondary,
            fontFamily: font(),
            fontWeight: 500,
          }}>
            {likeCount}
          </Typography>
        </ButtonBase>
      )}

      <IconButton
        size="small"
        disabled={disabled}
        onClick={(e) => { e.stopPropagation(); onCommentClick(); }}
        sx={{
          color: theme.palette.text.secondary,
          p: dense ? 0.5 : 0.75,
          '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
        }}
        aria-label="View comments"
      >
        <ChatCircleIcon size={iconSize} />
      </IconButton>
      {commentCount > 0 && (
        <ButtonBase
          onClick={(e) => { e.stopPropagation(); onCommentClick(); }}
          disabled={disabled}
          sx={{
            borderRadius: 1,
            px: 0.75,
            py: 0.5,
            '&:hover': !disabled
              ? { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' }
              : undefined,
          }}
        >
          <Typography sx={{
            fontSize,
            color: theme.palette.text.secondary,
            fontFamily: font(),
            fontWeight: 500,
          }}>
            {commentCount}
          </Typography>
        </ButtonBase>
      )}
    </Box>
  );
};

export default PostActionBar;
